import posthog from 'posthog-js/dist/module.full.no-external'

/**
 * PostHog's `distinct_id` is the person every event is attributed to.
 * `identify` and `reset` both change it, and PostHog's experiment queries
 * inner-join metric events to the exposure event on the person, so a variant
 * that is only registered as a super property is not enough: each person has to
 * carry its own `experiment_exposed`.
 *
 * posthog-js has no subscription for this, so `track.ts` announces the two
 * calls that change it and `useExperimentExposure` listens.
 *
 * Imports nothing but PostHog on purpose, like `experiment-properties.ts`: the
 * app logger pulls in env validation and breaks the component tests that reach
 * this module.
 */
const listeners = new Set<() => void>()

let version = 0

/** Subscribes to identity changes. Returns the unsubscribe function. */
export function subscribeAnalyticsIdentity(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Announces that `identify` or `reset` just ran, or that consent changed. */
export function notifyAnalyticsIdentityChanged() {
  version += 1
  for (const listener of listeners) listener()
}

/**
 * Counts the announcements so far. `useSyncExternalStore` subscribers watch
 * this rather than the distinct id, because an announcement that leaves the
 * person unchanged still matters: an opt-in keeps the same `distinct_id`, and
 * a subscriber watching only the id would never re-run.
 * @internal
 */
export function getAnalyticsIdentityVersion() {
  return version
}

/** The current person, or undefined before PostHog is initialized. */
export function getAnalyticsDistinctId() {
  try {
    return posthog.get_distinct_id()
  } catch {
    return undefined
  }
}
